import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  Loader2,
  FileDown,
  FileText,
  Play,
  Pause,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';
import {
  useInventoryCount,
  useInventoryCountItems,
  useInventoryCountsRealtime,
} from './useInventoryCounts';
import {
  formatDate,
  categorizeItems,
  calculateTotals,
  generateCSV,
  downloadFile,
} from './utils';
import type { DetailTab, InventoryCountItem } from './types';

export default function InventoryCountDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();

  useInventoryCountsRealtime();

  const { data: count, isLoading: countLoading } = useInventoryCount(id);
  const { data: items = [], isLoading: itemsLoading } = useInventoryCountItems(id);

  const [activeTab, setActiveTab] = useState<DetailTab>('all');
  const [showDetails, setShowDetails] = useState(false);

  // ── Derived data ───────────────────────────
  const categorized = useMemo(() => categorizeItems(items), [items]);

  const tabCounts = useMemo(
    () => ({
      uncounted: categorized.uncounted.length,
      unmatched: categorized.unmatched.length,
      matched: categorized.matched.length,
      all: categorized.all.length,
    }),
    [categorized]
  );

  const currentItems = useMemo(() => {
    switch (activeTab) {
      case 'uncounted':
        return categorized.uncounted;
      case 'unmatched':
        return categorized.unmatched;
      case 'matched':
        return categorized.matched;
      case 'all':
      default:
        return categorized.all;
    }
  }, [activeTab, categorized]);

  const totals = useMemo(() => calculateTotals(currentItems), [currentItems]);
  const allTotals = useMemo(() => calculateTotals(items), [items]);

  // ── Product filter badges (partial counts) ──
  const filterBadges = useMemo(() => {
    if (!count || count.count_type !== 'partial') return [];
    // Show up to 8 unique products that are in this count
    return items
      .filter((item) => item.product?.name)
      .slice(0, 8)
      .map((item) => ({
        id: item.product_id,
        name: item.product!.name,
      }));
  }, [count, items]);

  // ── Tabs config ────────────────────────────
  const tabs: { id: DetailTab; label: string; count: number }[] = [
    { id: 'uncounted', label: 'Uncounted', count: tabCounts.uncounted },
    { id: 'unmatched', label: 'Unmatched', count: tabCounts.unmatched },
    { id: 'matched', label: 'Matched', count: tabCounts.matched },
    { id: 'all', label: 'All', count: tabCounts.all },
  ];

  // ── Report generation ──────────────────────
  const handleCSVExport = () => {
    if (!count) return;
    const csv = generateCSV(items, count.name || count.count_number, count.location?.name || '');
    downloadFile(
      csv,
      `inventory-count-${count.count_number}.csv`,
      'text/csv;charset=utf-8;'
    );
  };

  const handlePDFExport = () => {
    if (!count) return;

    // Build a printable HTML document and use window.print()
    const countName = count.name || count.count_number;
    const locationName = count.location?.name || '';
    const startDate = count.started_at ? formatDate(count.started_at) : 'N/A';

    const tableRows = items
      .map(
        (item) => `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${item.product?.name || 'Unknown'}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;">${item.product?.sku || '-'}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right;">${item.expected_quantity}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right;">${item.counted_quantity ?? '-'}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right;">${item.variance ?? '-'}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right;">${item.variance_cost != null ? formatCurrency(item.variance_cost) : '-'}</td>
      </tr>`
      )
      .join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Inventory Count Report - ${countName}</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, sans-serif; padding: 40px; color: #333; }
          h1 { font-size: 20px; margin-bottom: 4px; }
          .meta { color: #666; font-size: 13px; margin-bottom: 24px; }
          table { width: 100%; border-collapse: collapse; font-size: 13px; }
          th { text-align: left; padding: 8px; border-bottom: 2px solid #333; font-weight: 600; }
          th.right { text-align: right; }
          .totals td { font-weight: 600; border-top: 2px solid #333; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>${countName}</h1>
        <div class="meta">
          <p>Location: ${locationName} | Start: ${startDate} | Type: ${count.count_type === 'full' ? 'Full' : 'Partial'}</p>
          <p>Generated: ${new Date().toLocaleString()}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>SKU</th>
              <th class="right">Expected</th>
              <th class="right">Counted</th>
              <th class="right">Variance</th>
              <th class="right">Cost</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
          <tfoot>
            <tr class="totals">
              <td colspan="2" style="padding:8px;">Total</td>
              <td style="padding:8px;text-align:right;">${allTotals.totalExpected}</td>
              <td style="padding:8px;text-align:right;">${allTotals.totalCounted}</td>
              <td style="padding:8px;text-align:right;">${allTotals.totalVarianceUnits}</td>
              <td style="padding:8px;text-align:right;">${formatCurrency(allTotals.totalVarianceCost)}</td>
            </tr>
          </tfoot>
        </table>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 300);
    }
  };

  // ── Loading ────────────────────────────────
  if (countLoading || itemsLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading count details...</span>
        </div>
      </div>
    );
  }

  if (!count) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Count not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/stock-control/counts')}>
          Back to counts
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* ── Header ──────────────────────────── */}
      <div className="flex items-start gap-3 mb-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 mt-0.5 shrink-0"
          onClick={() => navigate('/stock-control/counts')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-lg font-semibold truncate">
              {count.name || count.count_number}
            </h2>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm text-primary hover:underline whitespace-nowrap"
            >
              {showDetails ? 'Hide details' : 'Show details'}
            </button>
          </div>

          {showDetails && (
            <div className="flex items-center gap-6 mt-2 text-sm">
              <div>
                <span className="text-muted-foreground">Start</span>
                <p className="font-medium">
                  {count.started_at ? formatDate(count.started_at) : formatDate(count.scheduled_at)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Outlet</span>
                <p className="font-medium">{count.location?.name}</p>
              </div>
              {count.completed_at && (
                <div>
                  <span className="text-muted-foreground">Completed</span>
                  <p className="font-medium">{formatDate(count.completed_at)}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Product filter badges ─────────── */}
      {filterBadges.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-4 ml-11">
          {filterBadges.map((badge) => (
            <Badge key={badge.id} variant="secondary" className="text-xs">
              Product: {badge.name}
            </Badge>
          ))}
          {items.length > 8 && (
            <span className="text-xs text-muted-foreground">
              +{items.length - 8} more
            </span>
          )}
        </div>
      )}

      {/* ── Linkage info ──────────────────── */}
      {count.linked_entity_type && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 mb-4 ml-11">
          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-900 dark:text-blue-100">
              Linked to{' '}
              {count.linked_entity_type === 'purchase_order'
                ? `Purchase Order: ${count.purchase_order?.order_number || ''}`
                : `Goods Transfer: ${count.goods_request?.transfer_number || ''}`}
            </p>
            {count.linked_entity_notes && (
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                {count.linked_entity_notes}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── Action bar ────────────────────── */}
      <div className="flex items-center justify-between gap-4 mb-6 ml-11">
        <p className="text-sm text-muted-foreground">
          View and export your count as a report
        </p>
        <div className="flex items-center gap-2">
          {count.status === 'in_progress' && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/stock-control/counts')}
              >
                <Pause className="h-3.5 w-3.5 mr-1.5" />
                Pause
              </Button>
              <Button
                variant="blaze"
                size="sm"
                onClick={() => navigate(`/stock-control/counts/${count.id}/count`)}
              >
                <Play className="h-3.5 w-3.5 mr-1.5" />
                Continue Counting
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={handlePDFExport}>
            <FileText className="h-3.5 w-3.5 mr-1.5" />
            Generate PDF report
          </Button>
          <Button variant="outline" size="sm" onClick={handleCSVExport}>
            <FileDown className="h-3.5 w-3.5 mr-1.5" />
            Generate CSV report
          </Button>
        </div>
      </div>

      {/* ── Variance summary (completed counts) ── */}
      {count.status === 'completed' && (
        <div className="grid grid-cols-4 gap-4 p-4 rounded-lg bg-muted/50 mb-6 ml-11">
          <div>
            <p className="text-sm text-muted-foreground">Products Counted</p>
            <p className="text-lg font-semibold">{count.total_products_counted}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Expected Qty</p>
            <p className="text-lg font-semibold">
              {count.total_expected_quantity?.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Counted Qty</p>
            <p className="text-lg font-semibold">
              {count.total_counted_quantity?.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Variance</p>
            <p
              className={cn(
                'text-lg font-semibold',
                (count.total_variance || 0) < 0
                  ? 'text-red-600'
                  : (count.total_variance || 0) > 0
                    ? 'text-green-600'
                    : ''
              )}
            >
              {(count.total_variance || 0) > 0 ? '+' : ''}
              {count.total_variance?.toLocaleString()} (
              {formatCurrency(count.total_variance_cost || 0)})
            </p>
          </div>
        </div>
      )}

      {/* ── Sub-tabs ──────────────────────── */}
      <div className="border-b mb-4 ml-11">
        <nav className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
              )}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </nav>
      </div>

      {/* ── Items table ───────────────────── */}
      <div className="ml-11">
        {currentItems.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p>No items in this category.</p>
          </div>
        ) : (
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                    Product
                  </th>
                  {/* COUNT LIST column group */}
                  {(activeTab === 'uncounted' || activeTab === 'all') && (
                    <th
                      className="text-center text-xs font-medium text-muted-foreground px-4 py-1 border-b"
                      colSpan={1}
                    />
                  )}
                  {/* INVENTORY COUNT column group */}
                  <th className="text-right text-sm font-medium text-muted-foreground px-4 py-3">
                    Expected
                  </th>
                  <th className="text-right text-sm font-medium text-muted-foreground px-4 py-3">
                    Total
                  </th>
                  {/* DIFFERENCES columns */}
                  {(activeTab === 'unmatched' || activeTab === 'all') && (
                    <>
                      <th className="text-right text-sm font-medium text-muted-foreground px-4 py-3">
                        Unit
                      </th>
                      <th className="text-right text-sm font-medium text-muted-foreground px-4 py-3">
                        Cost
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {currentItems.map((item) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    showDifferences={activeTab === 'unmatched' || activeTab === 'all'}
                    formatCurrency={formatCurrency}
                  />
                ))}
              </tbody>
              {/* Totals row */}
              <tfoot className="bg-muted/30 border-t-2">
                <tr>
                  <td className="px-4 py-3 font-semibold text-sm">Total</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-sm">
                    {totals.totalExpected}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-sm">
                    {totals.totalCounted}
                    {totals.countedItems < totals.totalItems && (
                      <AlertTriangle className="h-3.5 w-3.5 inline ml-1 text-amber-500" />
                    )}
                  </td>
                  {(activeTab === 'unmatched' || activeTab === 'all') && (
                    <>
                      <td
                        className={cn(
                          'px-4 py-3 text-right tabular-nums font-semibold text-sm',
                          totals.totalVarianceUnits < 0
                            ? 'text-red-600'
                            : totals.totalVarianceUnits > 0
                              ? 'text-green-600'
                              : ''
                        )}
                      >
                        {totals.totalVarianceUnits}
                      </td>
                      <td
                        className={cn(
                          'px-4 py-3 text-right tabular-nums font-semibold text-sm',
                          totals.totalVarianceCost < 0
                            ? 'text-red-600'
                            : totals.totalVarianceCost > 0
                              ? 'text-green-600'
                              : ''
                        )}
                      >
                        {formatCurrency(totals.totalVarianceCost)}
                      </td>
                    </>
                  )}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// Item row
// ──────────────────────────────────────────────

function ItemRow({
  item,
  showDifferences,
  formatCurrency,
}: {
  item: InventoryCountItem;
  showDifferences: boolean;
  formatCurrency: (amount: number) => string;
}) {
  return (
    <tr className="hover:bg-muted/30 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {item.product?.image_url ? (
            <img
              src={item.product.image_url}
              alt={item.product.name}
              className="w-8 h-8 rounded object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
              <Package className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
          <div>
            <p className="font-medium text-sm">{item.product?.name || 'Unknown'}</p>
            {item.product?.sku && (
              <p className="text-xs text-muted-foreground">{item.product.sku}</p>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-sm">{item.expected_quantity}</td>
      <td className="px-4 py-3 text-right tabular-nums text-sm">
        {item.counted_quantity !== null ? item.counted_quantity : '-'}
      </td>
      {showDifferences && (
        <>
          <td
            className={cn(
              'px-4 py-3 text-right tabular-nums text-sm font-medium',
              (item.variance ?? 0) < 0
                ? 'text-red-600'
                : (item.variance ?? 0) > 0
                  ? 'text-green-600'
                  : ''
            )}
          >
            {item.variance !== null ? item.variance : '-'}
          </td>
          <td
            className={cn(
              'px-4 py-3 text-right tabular-nums text-sm font-medium',
              (item.variance_cost ?? 0) < 0
                ? 'text-red-600'
                : (item.variance_cost ?? 0) > 0
                  ? 'text-green-600'
                  : ''
            )}
          >
            {item.variance_cost !== null ? formatCurrency(item.variance_cost) : '-'}
          </td>
        </>
      )}
    </tr>
  );
}
